import Button from '_/space-management/components/Button';
import DropdownInput from '_/space-management/components/DropdownInput';
import Text from '_/space-management/components/Text';
import Title from '_/space-management/components/Title';
import { colors, spacing } from '_/space-management/constants/theme';
import { useDateContext } from '_/space-management/hooks/DateContext';
import { useEventContext } from '_/space-management/hooks/EventContext';
import { useSpaceContext } from '_/space-management/hooks/SpaceContext';
import { RecurrenceType } from '_/space-management/services/models/enums/recurrence-type.enum';
import { getSpaceTypeColor } from '_/space-management/services/models/enums/space-type.enum';
import { WeekDaysTypes } from '_/space-management/services/models/events.model';
import { getDay } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, View, StyleSheet, TextInput, Platform } from 'react-native';
import Modal from 'react-native-modal';
import { RRule, Weekday } from 'rrule';
import { fontFamily, fontSizes } from '_/config/theme';

import FinalDateCalendarModal from './FinalDateCalendar';
import RecurrenceFinishDateSelection from './RecurrenceFinishDate';
import WeekDays from './WeekDays';
interface CustomRecurrenceModalProps {
  visible: boolean;
  setCustomModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setRecurrenceType: (type: RecurrenceType) => void;
}

export default function CustomRecurrence({
  visible,
  setRecurrenceType,
  setCustomModalVisible,
}: CustomRecurrenceModalProps) {
  const { selectedFinalDayToDate, selectedEventDate } = useDateContext();
  const { t } = useTranslation();
  const {
    setByWeekDay,
    setRecurrenceInterval,
    recurrenceInterval,
    setFreq,
    byWeekDay,
    freq,
    setRecurrenceStr,
  } = useEventContext();
  const { spaceType } = useSpaceContext();
  const [selected, setSelected] = useState('endOfYear');
  const [finalDateCalendarModalVisible, setFinalDateCalendarModalVisible] = useState(false);
  const [selectedDays, setSelectedDay] = useState<WeekDaysTypes[]>([
    {
      id: 0,
      name: t('spaceManage.weekDays.mon'),
      selected: getDay(selectedEventDate.start) === 1,
      value: RRule.MO,
      day: 1,
    },
    {
      id: 1,
      name: t('spaceManage.weekDays.tue'),
      selected: getDay(selectedEventDate.start) === 2,
      value: RRule.TU,
      day: 2,
    },
    {
      id: 2,
      name: t('spaceManage.weekDays.wed'),
      selected: getDay(selectedEventDate.start) === 3,
      value: RRule.WE,
      day: 3,
    },
    {
      id: 3,
      name: t('spaceManage.weekDays.thu'),
      selected: getDay(selectedEventDate.start) === 4,
      value: RRule.TH,
      day: 4,
    },
    {
      id: 4,
      name: t('spaceManage.weekDays.fri'),
      selected: getDay(selectedEventDate.start) === 5,
      value: RRule.FR,
      day: 5,
    },
    {
      id: 5,
      name: t('spaceManage.weekDays.sat'),
      selected: getDay(selectedEventDate.start) === 6,
      value: RRule.SA,
      day: 6,
    },
    {
      id: 6,
      name: t('spaceManage.weekDays.sun'),
      selected: getDay(selectedEventDate.start) === 0,
      value: RRule.SU,
      day: 0,
    },
  ]);

  const handleCustomRecurrence = () => {
    const rule = new RRule({
      freq,
      interval: Number(recurrenceInterval),
      byweekday: byWeekDay?.map((item: { value: Weekday }) => item.value),
      dtstart: new Date(
        Date.UTC(
          selectedEventDate.start.getFullYear(),
          selectedEventDate.start.getMonth(),
          selectedEventDate.start.getDate(),
          selectedEventDate.start.getHours(),
          selectedEventDate.start.getMinutes(),
          selectedEventDate.start.getSeconds()
        )
      ),
      until: selectedFinalDayToDate,
    });
    setRecurrenceStr(rule.toString());
    setRecurrenceType(RecurrenceType.CUSTOM);
    setCustomModalVisible(false);
  };

  useEffect(() => {
    if (selectedDays) {
      const filterSelectedTrue = selectedDays.filter((item) => item.selected === true);
      setByWeekDay(filterSelectedTrue);
    }
  }, [selectedDays, setByWeekDay]);

  return (
    <>
      <Modal
        style={styles.modal}
        animationIn="slideInUp"
        animationOut="slideOutDown"
        isVisible={visible}
        coverScreen
        hasBackdrop
        backdropOpacity={0.5}
        onBackdropPress={() => setCustomModalVisible(false)}
        onBackButtonPress={() => setCustomModalVisible(false)}
        onSwipeStart={() => setCustomModalVisible(false)}
      >
        <Title
          title={t('spaceManage.workstationScreen.customRecurrenceModal.custom')}
          hasCloseButton
          onPress={() => setCustomModalVisible(false)}
        />
        <Text fontSize={fontSizes.md2} fontType="bold" style={styles.text}>
          {t('spaceManage.daysOfWeek')}
        </Text>
        <WeekDays selectedDays={selectedDays} setSelectedDays={setSelectedDay} />
        <Text fontSize={fontSizes.md2} fontType="bold" style={styles.text}>
          {t('spaceManage.workstationScreen.customRecurrenceModal.repeatEvery')}
        </Text>
        <View style={[styles.repeat, Platform.OS !== 'android' && { zIndex: 99999 }]}>
          <TextInput
            style={styles.dayInput}
            keyboardType="numeric"
            value={recurrenceInterval}
            onChangeText={setRecurrenceInterval}
          />
          <DropdownInput
            value={freq}
            setValue={setFreq}
            options={[
              { label: t('spaceManage.day'), value: RRule.DAILY },
              { label: t('spaceManage.week'), value: RRule.WEEKLY },
              { label: t('spaceManage.month'), value: RRule.MONTHLY },
            ]}
            textStyle={styles.textStyle}
            dropDownContainerStyle={styles.dropdownContainerStyle}
            dropdownStyle={styles.dropdownStyle}
          />
        </View>
        <Text fontSize={fontSizes.md2} fontType="bold" style={styles.text}>
          {t('spaceManage.workstationScreen.customRecurrenceModal.endsAt')}
        </Text>
        <RecurrenceFinishDateSelection
          selected={selected}
          setSelected={setSelected}
          setFinalDateCalendarModalVisible={setFinalDateCalendarModalVisible}
        />
        <View style={styles.button}>
          <Button
            backgroundColor={getSpaceTypeColor(spaceType)}
            textColor={colors.white}
            onPress={handleCustomRecurrence}
          >
            {t('spaceManage.workstationScreen.customRecurrenceModal.confirmCustomization')}
          </Button>
        </View>
        <FinalDateCalendarModal
          visible={finalDateCalendarModalVisible}
          setFinalDateCalendarModalVisible={setFinalDateCalendarModalVisible}
        />
      </Modal>
    </>
  );
}

const { width } = Dimensions.get('window');

const styles = StyleSheet.create({
  modal: {
    width: '100%',
    ...(Platform.OS === 'web' && { maxWidth: 768 }),
    margin: 0,
    marginTop: 120,
    backgroundColor: '#fff',
    alignSelf: 'center',
    justifyContent: 'flex-start',
    borderTopRightRadius: 20,
    borderTopLeftRadius: 20,
    paddingVertical: 40,
  },
  text: {
    marginHorizontal: spacing.md,
    marginBottom: spacing.md,
    marginTop: spacing.md,
  },
  dayInput: {
    textAlign: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderWidth: 1,
    borderColor: colors.lightGrey,
    borderRadius: 8,
    marginRight: spacing.xs,
    fontFamily: fontFamily.bold,
    fontSize: fontSizes.lg2,
    ...(Platform.OS === 'web' && {
      maxWidth: 768 * 0.2,
    }),
  },
  repeat: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    width,
    maxWidth: 768,
  },
  button: {
    marginHorizontal: spacing.md,
    marginTop: spacing.md,
  },
  dropdownStyle: {
    borderTopLeftRadius: 8,
    width: Platform.OS === 'web' ? 768 * 0.6 : width * 0.75,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    height: 60,
    flexDirection: 'row',
  },
  dropdownContainerStyle: {
    height: 'auto',
    width: Platform.OS === 'web' ? 768 * 0.6 : width * 0.75,
    borderRadius: 8,
    display: 'flex',
  },
  textStyle: {
    fontSize: fontSizes.lg2,
    textAlign: 'left',
    color: colors.black,
    fontFamily: fontFamily.bold,
  },
});
